import { RefObject, useEffect, useRef, useState } from 'react';

class PFIntersectionObserver {
  private map: Map<Element | null, () => void>;
  private observer: IntersectionObserver | null = null;

  constructor(options: IntersectionObserverInit | undefined) {
    this.map = new Map();
    if (typeof document !== 'undefined') {
      this.observer = new IntersectionObserver(
        this.intersectCallback.bind(this),
        options
      );
    }
  }

  observe(target: RefObject<HTMLElement>, cb: () => void) {
    this.map.set(target.current, cb);
    if (this.observer != null) {
      this.observer.observe(target.current as HTMLElement);
    }
  }

  unobserve(target: RefObject<HTMLElement>) {
    this.map.delete(target.current);
    if (this.observer) {
      this.observer.unobserve(target.current as HTMLElement);
    }
  }

  intersectCallback(entries: IntersectionObserverEntry[]) {
    entries
      .filter((entry) => entry.isIntersecting)
      .forEach((entry) => {
        const onVisible = this.map.get(entry.target);
        if (onVisible) {
          onVisible();
        }
      });
  }
}

let observer: null | PFIntersectionObserver = null;

export function useIntersectionObserver(): {
  ref: React.MutableRefObject<HTMLElement | null>;
  visible: boolean;
} {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(process.env.NODE_ENV === 'test');

  if (!observer) {
    observer = new PFIntersectionObserver({
      rootMargin: '100px',
    });
  }

  useEffect(() => {
    observer?.observe(ref, () => setVisible(true));
    return () => {
      observer?.unobserve(ref);
    };
  }, []);

  return { ref, visible };
}
